/*
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */
import { useExtensions } from '@adobe/uix-host-react'
import { useState } from 'react'
import { updateMenu } from './utils'

interface MenuRegistrationProps {
  url: string
  rootElmSelector: string
}

function sortBySectionsFn(
  lhs: { isSection: boolean },
  rhs: { isSection: boolean }
) {
  if (lhs.isSection) {
    return -1
  }
  if (!lhs.isSection && rhs.isSection) {
    return 1
  }
  return 0
}

export const Menu = ({ url, rootElmSelector }: MenuRegistrationProps) => {
  const { extensions } = useExtensions(() => ({
    updateOn: 'all',
    requires: {
      menu: ['getItems']
    }
  }))

  const [isUpdated, setIsUpdated] = useState(false)

  if (extensions.length === 0 && !isUpdated) {
    updateMenu(url, rootElmSelector, [])
    setIsUpdated(true)
  } else {
    extensions.map(async ({ apis, id }) => {
      const items = await apis.menu.getItems()
      const completedItems = items.map((item: { isSection: boolean }) => ({
        ...item,
        action: item.isSection ? null : `adminuisdk/menu/page/extensionId/${id}`
      }))
      const sortedMenuItems = [...completedItems].sort((lhs, rhs) =>
        sortBySectionsFn(lhs, rhs)
      )
      updateMenu(url, rootElmSelector, sortedMenuItems.flat())
    })
  }
}
